import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { Roles } from '@/api/core-api';
import {
  type CoreApiMutationResult,
  toCoreApiMutationResult,
} from '@/utils/core-api-mutation-result';
import { serverFetch } from '@/utils/server-fetch';
import { getServerSession } from '@/utils/session/get-server-session';

/**
 * Server functions behind the "Your teams" settings page, a port of the legacy
 * team switcher (`website/.../dashboard/components/team-switcher`). They read
 * the same v4 organization and team endpoints and shape the answer here, so
 * member lists and other users' ids never reach the browser: only names and
 * the request ids an admin needs to act on.
 *
 * core-api authorizes every call against the `X-Current-UserId` header, so the
 * organization and team ids the client passes cannot widen what a researcher
 * may see or change.
 */

export interface JoinRequestSummary {
  id: string;
  userName: string;
}

export interface MyTeam {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  /** Team owner or admin: may approve or deny join requests. */
  canManage: boolean;
  /** Only filled for teams the researcher can manage. */
  pendingJoinRequests: JoinRequestSummary[];
}

export interface OtherTeam {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  hasPendingRequest: boolean;
}

export interface NewTeamRequestSummary {
  id: string;
  teamName: string;
  description: string;
  userName: string;
}

export type TeamsOverviewOrganization = {
  id: string;
  name: string;
  /** Organization owner or admin. */
  isAdmin: boolean;
  /** Only filled for organization owners and admins. */
  pendingNewTeamRequests: NewTeamRequestSummary[];
} & (
  | { teamsUnavailable: false; myTeams: MyTeam[]; otherTeams: OtherTeam[] }
  | { teamsUnavailable: true }
);

export type TeamsOverview = { organizations: TeamsOverviewOrganization[] };

export type TeamsMutationResult = CoreApiMutationResult;

const idSchema = z.union([z.string(), z.number()]).transform(String);

const rawRequestSchema = z.object({
  id: idSchema.optional(),
  _id: idSchema.optional(),
  status: z.string().nullish(),
  userName: z.string().nullish(),
});

const rawNewTeamRequestSchema = rawRequestSchema.extend({
  teamName: z.string().nullish(),
  description: z.string().nullish(),
});

const rawMemberSchema = z.object({
  userId: idSchema,
  roles: z.array(z.string()).nullish(),
  isActive: z.boolean().nullish(),
});

const rawOrganizationSchema = z.object({
  id: idSchema.optional(),
  _id: idSchema.optional(),
  name: z.string().nullish(),
  privateTeams: z.boolean().nullish(),
  users: z.array(rawMemberSchema).nullish().catch(undefined),
  newTeamRequests: z.array(rawNewTeamRequestSchema).nullish().catch(undefined),
});

const rawTeamSchema = z.object({
  id: idSchema.optional(),
  _id: idSchema.optional(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  imageUrl: z.string().nullish(),
  isDeleted: z.boolean().nullish(),
  isActive: z.boolean().nullish(),
  members: z.array(rawMemberSchema).nullish().catch(undefined),
  joinRequests: z.array(rawRequestSchema).nullish().catch(undefined),
});

type RawMember = z.infer<typeof rawMemberSchema>;

const ADMIN_ROLES = new Set(['owner', 'admin']);

function idOf(value: { id?: string; _id?: string }): string | undefined {
  return value.id ?? value._id;
}

function isAdminMember(
  members: RawMember[] | null | undefined,
  userId: string,
): boolean {
  return (
    members?.some(
      (member) =>
        member.userId === userId &&
        member.roles?.some((role) => ADMIN_ROLES.has(role)) === true,
    ) ?? false
  );
}

function asArray(json: unknown): unknown[] {
  return Array.isArray(json) ? json : [json];
}

async function requireResearcher(): Promise<{
  id: string;
  teamIds: Set<string>;
} | null> {
  const session = await getServerSession();
  const user = session?.user;
  if (!user || !user.roles?.includes(Roles.RESEARCHER)) return null;
  return { id: user.id, teamIds: new Set(user.teams?.map((t) => t.teamId)) };
}

async function requireResearcherId(): Promise<string | null> {
  const researcher = await requireResearcher();
  return researcher?.id ?? null;
}

const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name);

async function loadOrganization(
  rawOrg: z.infer<typeof rawOrganizationSchema>,
  userId: string,
  memberTeamIds: Set<string>,
): Promise<TeamsOverviewOrganization | null> {
  const orgId = idOf(rawOrg);
  if (!orgId) return null;

  // Owners and admins get the full organization document, which is the only
  // one carrying `users`; everyone else gets a sanitized copy without it.
  const isAdmin = isAdminMember(rawOrg.users, userId);
  const pendingNewTeamRequests: NewTeamRequestSummary[] = isAdmin
    ? (rawOrg.newTeamRequests ?? []).flatMap((request) => {
        const id = idOf(request);
        if (!id || request.status !== 'pending') return [];
        return [
          {
            id,
            teamName: request.teamName ?? '',
            description: request.description ?? '',
            userName: request.userName ?? '',
          },
        ];
      })
    : [];

  const base = {
    id: orgId,
    name: rawOrg.name ?? 'Untitled organization',
    isAdmin,
    pendingNewTeamRequests,
  };

  const orgPath = `/v4/organizations/${encodeURIComponent(orgId)}`;
  const [teamsResponse, requestedResponse] = await Promise.all([
    serverFetch({ path: `${orgPath}/teams`, userId }),
    serverFetch({
      path: `${orgPath}/teams/join-requests?status=pending`,
      userId,
    }),
  ]).catch((error: unknown) => {
    console.error('getTeamsOverview: team lookup failed', orgId, error);
    return [null, null];
  });

  if (!teamsResponse?.ok) {
    return { ...base, teamsUnavailable: true };
  }

  const teams = asArray(await teamsResponse.json()).flatMap((json) => {
    const parsed = rawTeamSchema.safeParse(json);
    return parsed.success ? [parsed.data] : [];
  });

  // The pending list only decides a "Pending" label; losing it must not hide
  // the teams themselves.
  const requestedTeamIds = new Set<string>();
  if (requestedResponse?.ok) {
    for (const json of asArray(await requestedResponse.json())) {
      const parsed = rawTeamSchema.safeParse(json);
      const id = parsed.success ? idOf(parsed.data) : undefined;
      if (id) requestedTeamIds.add(id);
    }
  }

  const myTeams: MyTeam[] = [];
  const otherTeams: OtherTeam[] = [];
  for (const team of teams) {
    const id = idOf(team);
    if (!id || !team.name || team.isDeleted === true) continue;

    const summary = {
      id,
      name: team.name,
      description: team.description ?? '',
      ...(team.imageUrl ? { imageUrl: team.imageUrl } : {}),
    };
    // Membership comes from the session user, the same list the team switch
    // (`persistActiveDashboard`) checks, so every team listed here as yours
    // can actually be switched to.
    const isMember = memberTeamIds.has(id);

    if (isMember) {
      const canManage = isAdminMember(team.members, userId);
      myTeams.push({
        ...summary,
        canManage,
        // Only owners and admins are sent `joinRequests` at all.
        pendingJoinRequests: canManage
          ? (team.joinRequests ?? []).flatMap((request) => {
              const requestId = idOf(request);
              if (!requestId || request.status !== 'pending') return [];
              return [{ id: requestId, userName: request.userName ?? '' }];
            })
          : [],
      });
    } else if (!rawOrg.privateTeams || isAdmin) {
      // With private teams, only organization owners and admins see teams
      // they are not on, matching core-api's team search. The v4 team list
      // used here does not apply that rule itself.
      otherTeams.push({
        ...summary,
        hasPendingRequest: requestedTeamIds.has(id),
      });
    }
  }

  myTeams.sort(byName);
  otherTeams.sort(byName);

  return { ...base, teamsUnavailable: false, myTeams, otherTeams };
}

export const getTeamsOverview = createServerFn().handler(
  async (): Promise<TeamsOverview> => {
    const researcher = await requireResearcher();
    if (!researcher) throw new Error('Not signed in as a researcher');
    const userId = researcher.id;

    const response = await serverFetch({ path: '/v4/organizations', userId });
    if (!response.ok) {
      throw new Error(`Failed to load organizations (${response.status})`);
    }

    const rawOrgs = asArray(await response.json()).flatMap((json) => {
      const parsed = rawOrganizationSchema.safeParse(json);
      return parsed.success ? [parsed.data] : [];
    });

    const organizations = await Promise.all(
      rawOrgs.map((org) => loadOrganization(org, userId, researcher.teamIds)),
    );
    return {
      organizations: organizations.filter(
        (org): org is TeamsOverviewOrganization => org !== null,
      ),
    };
  },
);

const objectIdSchema = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');
const decisionSchema = z.enum(['approved', 'denied']);

export type RequestDecision = z.infer<typeof decisionSchema>;

const joinTeamSchema = z.object({
  organizationId: objectIdSchema,
  teamId: objectIdSchema,
});

/**
 * Asks to join a team. core-api adds organization owners and admins to the
 * team straight away instead of filing a request.
 */
export const requestToJoinTeam = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof joinTeamSchema>) =>
    joinTeamSchema.parse(data),
  )
  .handler(async ({ data }): Promise<TeamsMutationResult> => {
    const userId = await requireResearcherId();
    if (!userId) return { success: false };

    const response = await serverFetch({
      path: `/v4/organizations/${data.organizationId}/teams/${data.teamId}/join-requests`,
      method: 'POST',
      userId,
    });
    return toCoreApiMutationResult(response, 'requestToJoinTeam');
  });

const decideJoinRequestSchema = joinTeamSchema.extend({
  joinRequestId: objectIdSchema,
  status: decisionSchema,
});

export const decideJoinRequest = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof decideJoinRequestSchema>) =>
    decideJoinRequestSchema.parse(data),
  )
  .handler(async ({ data }): Promise<TeamsMutationResult> => {
    const userId = await requireResearcherId();
    if (!userId) return { success: false };

    const response = await serverFetch({
      path: `/v4/organizations/${data.organizationId}/teams/${data.teamId}/join-requests/${data.joinRequestId}`,
      method: 'PATCH',
      userId,
      body: JSON.stringify({ status: data.status }),
    });
    return toCoreApiMutationResult(response, 'decideJoinRequest');
  });

export const newTeamSchema = z.object({
  organizationId: objectIdSchema,
  teamName: z.string().trim().min(2).max(100),
  description: z.string().trim().min(1).max(500),
});

/**
 * Creates a team, or for anyone below organization admin, files a request
 * for the organization's owners and admins to approve.
 */
export const createTeamOrRequest = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof newTeamSchema>) => newTeamSchema.parse(data))
  .handler(async ({ data }): Promise<TeamsMutationResult> => {
    const userId = await requireResearcherId();
    if (!userId) return { success: false };

    const { organizationId, ...body } = data;
    const response = await serverFetch({
      path: `/v4/organizations/${organizationId}/new-team-requests`,
      method: 'POST',
      userId,
      body: JSON.stringify(body),
    });
    return toCoreApiMutationResult(response, 'createTeamOrRequest');
  });

const decideNewTeamRequestSchema = z.object({
  organizationId: objectIdSchema,
  requestId: objectIdSchema,
  status: decisionSchema,
});

export const decideNewTeamRequest = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof decideNewTeamRequestSchema>) =>
    decideNewTeamRequestSchema.parse(data),
  )
  .handler(async ({ data }): Promise<TeamsMutationResult> => {
    const userId = await requireResearcherId();
    if (!userId) return { success: false };

    const response = await serverFetch({
      path: `/v4/organizations/${data.organizationId}/new-team-requests/${data.requestId}`,
      method: 'PATCH',
      userId,
      body: JSON.stringify({ status: data.status }),
    });
    return toCoreApiMutationResult(response, 'decideNewTeamRequest');
  });
